import { useEffect, useRef, useState } from 'react';
import type { WorkspaceMemberInfo } from '../workspaces/workspacesApi';
import type { BoardColumn } from '../boardColumns/boardColumnsApi';
import type { Label } from '../labels/labelsApi';
import type { Task } from './tasksApi';
import { useIsMobile } from '../hooks/useIsMobile';

function abbrev(name: string): string {
  return name.trim().split(/\s+/).map(w => w[0]?.toUpperCase() ?? '').join('');
}

/** Compact dropdown showing initials when closed, "INIT — Full Name" when open. */
function StatusDropdown({
  columns,
  currentStatus,
  onStatusChange,
}: {
  columns: BoardColumn[];
  currentStatus: string;
  onStatusChange?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const current = columns.find(c => c.columnId === currentStatus);

  function toggle() {
    if (!onStatusChange) return;
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen(v => !v);
  }

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        dropRef.current && !dropRef.current.contains(target) &&
        btnRef.current && !btnRef.current.contains(target)
      ) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  return (
    <div style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        disabled={!onStatusChange}
        style={{
          padding: '2px 5px',
          fontSize: 10,
          fontWeight: 600,
          background: 'var(--color-primary)',
          color: '#fff',
          border: '1px solid var(--color-primary)',
          borderRadius: 3,
          cursor: onStatusChange ? 'pointer' : 'default',
          lineHeight: 1.4,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {current ? abbrev(current.name) : '?'}<span style={{ fontSize: 8 }}>▾</span>
      </button>

      {open && (
        <div
          ref={dropRef}
          style={{
            position: 'fixed',
            top: dropPos.top,
            right: dropPos.right,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
            zIndex: 400,
            minWidth: 160,
            overflow: 'hidden',
          }}
        >
          {columns.map(col => (
            <button
              key={col.columnId}
              type="button"
              onClick={() => { onStatusChange?.(col.columnId); setOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 12px',
                fontSize: 13,
                background: currentStatus === col.columnId ? 'rgba(37,99,235,0.08)' : 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--color-text)',
              }}
            >
              <span style={{
                fontWeight: 700,
                fontSize: 11,
                color: currentStatus === col.columnId ? 'var(--color-primary)' : 'var(--color-text-muted)',
                minWidth: 24,
              }}>
                {abbrev(col.name)}
              </span>
              {col.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface TaskListRowProps {
  task: Task;
  columns: BoardColumn[];
  labels: Label[];
  members: WorkspaceMemberInfo[];
  selected: boolean;
  onSelect: () => void;
  onStatusChange?: (status: string) => void;
}

export default function TaskListRow({
  task,
  columns,
  labels,
  members,
  selected,
  onSelect,
  onStatusChange,
}: TaskListRowProps) {
  const isMobile = useIsMobile();
  const firstLabel = labels.find(l => task.labelIds.includes(l.labelId));
  const borderColor = firstLabel?.color ?? 'var(--color-border)';
  const assignees = members.filter(m => task.assigneeIds.includes(m.userId));

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onSelect()}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 10px',
        borderLeft: `3px solid ${borderColor}`,
        background: selected ? 'rgba(59,130,246,0.08)' : undefined,
        cursor: 'pointer',
        borderBottom: '1px solid var(--color-border)',
        outline: 'none',
        userSelect: 'none',
        minHeight: 32,
      }}
    >
      {task.completedAt ? (
        <span style={{ color: '#22c55e', fontSize: 13, flexShrink: 0 }}>✓</span>
      ) : (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            border: '1.5px solid var(--color-border)',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
      )}

      <span
        style={{
          flex: 1,
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textDecoration: task.completedAt ? 'line-through' : undefined,
          color: task.completedAt ? 'var(--color-text-muted)' : undefined,
        }}
      >
        {task.title}
      </span>

      {task.complexity != null && task.complexity > 0 && (
        <span className="badge" style={{ background: '#7c3aed', color: '#fff', flexShrink: 0, fontSize: 10, padding: '1px 4px' }}>
          C{task.complexity}
        </span>
      )}

      {task.points > 0 && (
        <span className="badge" style={{ flexShrink: 0, fontSize: 10, padding: '1px 4px' }}>
          {task.points}pt
        </span>
      )}

      {assignees.length > 0 && (
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', flexShrink: 0 }}>
          {isMobile
            ? assignees.map(a => abbrev(a.displayName)).join(' ')
            : assignees.map(a => a.displayName.split(' ')[0]).join(', ')}
        </span>
      )}

      {/* Status control: compact dropdown on mobile, row of buttons on desktop */}
      {columns.length > 0 && (
        isMobile ? (
          <StatusDropdown
            columns={columns}
            currentStatus={task.status}
            onStatusChange={onStatusChange}
          />
        ) : (
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            {columns.map(col => (
              <button
                key={col.columnId}
                type="button"
                onClick={() => onStatusChange?.(col.columnId)}
                disabled={!onStatusChange}
                title={col.name}
                style={{
                  padding: '1px 4px',
                  fontSize: 10,
                  fontWeight: 600,
                  background: task.status === col.columnId ? 'var(--color-primary)' : 'transparent',
                  color: task.status === col.columnId ? '#fff' : 'var(--color-text-muted)',
                  border: `1px solid ${task.status === col.columnId ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  borderRadius: 3,
                  cursor: onStatusChange ? 'pointer' : 'default',
                  lineHeight: 1.4,
                }}
              >
                {abbrev(col.name)}
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}
